/* =============================================================
   Aëlvir — EN content for the single page.
   Mirrors content.fr.js exactly. FR remains the source of truth.
   Same editorial rules: no "training organisation" outside legal
   notices, in-person and headset rental are Cywiz delivery modes,
   nothing invented.
   ============================================================= */

export const en = {
  nav: {
    studio: "Studio",
    methode: "Method",
    contact: "Contact",
  },
  contactCta: "Discuss a project",

  common: {
    backToTop: "Back to top",
    skipToContent: "Skip to content",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    switchLang: "Switch language",
    home: "Aëlvir home",
    close: "Close",
    newWindow: "(opens in a new window)",
    logoAlt: "AËLVIR · Approach, Learn, Advance",
    cywizProductAlt: "Cywiz: dashboard and virtual reality headset",
  },

  seo: {
    title: "Aëlvir · Studio for serious games and virtual reality training",
    description:
      "Aëlvir builds training experiences, 2D/3D serious games and virtual reality. Publisher of Cywiz, a cybersecurity awareness application already deployed.",
  },

  org: {
    name: "Aëlvir",
    street: "7 Rue Auguste Rodin",
    city: "28630 Le Coudray",
    country: "France",
    email: "contact.aelvir@proton.me",
    phone: "06 60 98 48 16",
    url: "https://aelvir.fr",
  },

  hero: {
    motto: ["Approach", "Learn", "Advance"],
    titleLines: ["We open", "the doors", "of skills"],
    subtitle:
      "Aëlvir designs custom interactive experiences, from 2D/3D serious games to virtual reality.",
    primaryBtn: "Discover {cywiz}",
    secondaryBtn: "Discuss a project",
    scroll: "Scroll",
  },

  problem: {
    kicker: "The starting point",
    title: ["The message lands,", "The behaviour does not change"],
    desc:
      "Three situations come back in almost every project that reaches us. Three barriers that limit impact, and that we help overcome.",
    columns: ["The problem", "The consequence", "Our response"],
    rows: [
      {
        title: "A message that leaves no trace",
        problem:
          "Inform, train, raise awareness: the content is followed and viewed, but nothing really sticks.",
        consequenceTitle: "The information changes nothing",
        consequence:
          "It is received passively. Behaviours and awareness do not last.",
        responseTitle: "We create experiences that leave a mark",
        response:
          "Engaging mechanics, situational practice, immediate feedback: our content is lived, understood and remembered.",
      },
      {
        title: "A situation that is costly or hard to reproduce",
        problem:
          "Reality is expensive, rare, risky or simply inaccessible for training or experimenting.",
        consequenceTitle: "Few chances to repeat or understand",
        consequence:
          "Situational practice is rare and incomplete. Difficulties persist.",
        responseTitle: "We simulate reality, without risk or constraint",
        response:
          "Faithful environments, adjustable scenarios: anyone can practise, discover or progress safely.",
      },
      {
        title: "An audience you can no longer reach",
        problem:
          "Profiles, ages and contexts vary widely. Classic formats do not speak to everyone.",
        consequenceTitle: "Low engagement, invisible impact",
        consequence:
          "Attention drops quickly, and real effects remain hard to measure.",
        responseTitle: "We design experiences that make people want to act",
        response:
          "Immersive and playful formats, adaptable to each audience: everyone stays engaged, everyone progresses.",
      },
    ],
    conviction: {
      label: "Our conviction",
      title: ["The right message is not enough.", "You need the right experience."],
      text:
        "We design custom interactive experiences that turn an intention into a concrete, engaging and memorable experience.",
      cta: "Discover our approach",
    },
  },

  studio: {
    kicker: "The studio",
    title: ["What we", "build"],
    desc:
      "One project delivered on whichever medium serves the objective: headset, screen, tablet or browser, immersive or not. We do not sell a catalogue: we start from your procedures and your real cases.",
    idealLabel: "Ideal for",
    items: [
      {
        verb: "Design",
        title: "Serious games",
        hook: "Make people understand, care, and act.",
        desc: "Games where every mechanic maps to an explicit learning objective. If an element entertains without teaching, it is cut.",
        ideal: [
          "Awareness · learning",
          "Communication · behaviour",
          "Compliance · adherence",
          "Role-play · decision-making",
        ],
      },
      {
        verb: "Immerse",
        title: "Virtual reality & simulation",
        hook: "Train without the constraints of the real world.",
        desc: "The gesture at 1:1 scale, in a rebuilt environment. Repeatable as often as needed, with no risk and no downtime.",
        ideal: [
          "Gestures · procedures",
          "High-risk situations",
          "Decision-making",
          "Memory anchoring",
        ],
      },
      {
        verb: "Build",
        title: "Custom-built",
        hook: "When your need fits no ready-made box.",
        desc: "Experiences designed around your specific challenges. From idea to rollout, we build with you the most relevant solution.",
        ideal: [
          "Bespoke experience",
          "Job-specific simulation",
          "Hybrid device",
          "Innovative project",
        ],
      },
    ],
  },

  cywiz: {
    kicker: "Ready to deploy",
    tagline: "Our cybersecurity application, deployed in the field",
    lead:
      "{cywiz} is our own VR cybersecurity training solution, built by Aëlvir. Already rolled out in enterprises and training centres, it has proved its effectiveness.",
    validation:
      "Content validated by cybeRéponse, the regional cyber incident response centre (Centre-Val de Loire, France).",
    proofs: [
      {
        title: "Designed by Aëlvir",
        text: "A solution born from our studio method, from idea to rollout.",
      },
      {
        title: "Tested in real conditions",
        text: "Proven with real learners, in enterprise and in training.",
      },
      {
        title: "Deployed in the field",
        text: "Already live with organisations that train every day.",
      },
    ],
    bridge: {
      kicker: "What about you?",
      title: [
        "This expertise does not stop at {cywiz}",
        "Let's apply it to your project.",
      ],
      text:
        "Whatever your challenge, we put our expertise\nat your service to design the solution\nbest suited to your objectives.",
      cta: "Let's talk about your project",
    },
  },

  method: {
    kicker: "Method",
    title: ["Validate the idea", "before producing"],
    desc:
      "A project does not drift for lack of talent: it drifts when production starts before anyone has checked that the concept works.",
    steps: [
      { title: "Scoping", desc: "Goals, audiences, real constraints. Success indicators are set before design starts." },
      { title: "Playable prototype", desc: "A rough version, tested with real learners. That is where we fix things, while it is cheap." },
      { title: "Production", desc: "3D, audio, AI where relevant. Testable intermediate builds, no six-month tunnel." },
      { title: "Rollout", desc: "Go-live, facilitator training, then reading the traces to adjust." },
    ],
  },

  faq: {
    kicker: "Straight answers",
    title: ["What people", "actually ask us"],
    items: [
      {
        q: "Do you have a training catalogue?",
        a: "No, and that is deliberate. Aëlvir is a studio: we design and build programmes. The only ready-to-use product we publish is {cywiz}. For everything else we start from your procedures, your real cases and the mistakes that keep happening at your site, not from a shelf.",
      },
      {
        q: "{cywiz} or a custom build: how do I choose?",
        a: "If your subject is cybersecurity awareness, {cywiz} exists, it is tested, validated and deployable right away: there is no point rebuilding it. If your subject is a technical gesture, an internal procedure or a context specific to your organisation, it has to be built: that is exactly our job.",
      },
      {
        q: "How long before we have something playable?",
        a: "A playable prototype comes before heavy production. It has neither final assets nor final art: it answers one question only: does this exercise teach anything. If it does not convince, we fix it then, not three months later.",
      },
      {
        q: "What budget should we plan for a custom project?",
        a: "Every project has its own envelope. We scope before we quote: goals, media, content volume. A ballpark figure is shared early, without a fixed commitment on the first call.",
      },
      {
        q: "What hardware is needed to use your solutions?",
        a: "It depends on the medium: VR headset, PC, tablet or browser. For VR, compatible headsets are confirmed during scoping. Hardware details for {cywiz} live on cywiz.fr.",
      },
      {
        q: "Who provides the content and scenarios?",
        a: "Content starts from your procedures, real cases and subject-matter experts. We design and produce it with you: you bring the material, we turn it into an experience.",
      },
    ],
  },

  need: {
    kicker: "Do you have a specific need?",
    title: [
      "Every situation deserves a tailored solution.",
      "Let's talk about your project.",
    ],
    text:
      "Do you need training, a hard-to-reproduce situation, or a gesture to pass on? Let's discuss your challenges and explore the solution best suited to your objectives.",
    pillars: [
      {
        title: "A specific need?",
        text: "Business goals to reach.",
      },
      {
        title: "A complex situation to reproduce?",
        text: "We turn it into an effective experience.",
      },
      {
        title: "A gesture to pass on?",
        text: "We make it immersive, clear and memorable.",
      },
    ],
  },

  footerExtra: {
    tagline:
      "Studio for custom interactive experiences: 2D/3D serious games and virtual reality. Publisher of {cywiz}, a cybersecurity awareness application.",
  },

  contact: {
    kicker: "Contact us",
    title: ["Let's talk about", "your project"],
    desc: "Tell us about your need. We'll get back to you quickly to discuss it.",
    labels: {
      name: "Full name",
      company: "Organisation",
      email: "Work email",
      type: "Request type",
      message: "Message",
    },
    placeholders: {
      name: "Full name",
      company: "Company, association, training body, public sector…",
      email: "Work email",
      message: "Describe your project or your need…",
    },
    typePlaceholder: "Your need is about…",
    requestTypes: [
      { value: "cywiz", label: "Cywiz: rollout, licences, session" },
      { value: "sur-mesure", label: "A custom programme" },
      { value: "autre", label: "Other request" },
    ],
    promises: [
      {
        title: "Fast reply",
        text: "We get back to you within 48 hours.",
      },
      {
        title: "Guaranteed confidentiality",
        text: "Your information stays strictly confidential.",
      },
      {
        title: "No obligation",
        text: "A first exchange to advise you.",
      },
    ],
    secure: "Your data is secure",
    rgpd: {
      required: "* All fields are required.",
      summary: "GDPR information about processing your request",
      paragraphs: [
        "This form lets you contact Aëlvir for a general question, an information request or a project. The legal basis for processing is Article 6(1)(a) GDPR: your consent, expressed by voluntarily sending the form.",
        "All form fields are mandatory: they are needed to handle your request, which you cannot send without them.",
        "The data submitted is intended for Aëlvir, its internal staff responsible for handling requests, Infomaniak Network SA for website hosting and SMTP sending of the form, then Proton AG for storage of messages received in the contact mailbox. It may also be disclosed to any legally authorised person.",
        "The data is kept for the time needed to handle your request, then for no longer than three years from the last exchange. You may request deletion at any time.",
        "You have the right to access, rectify, erase, restrict, object to and port your data, as well as the right to withdraw your consent at any time. To exercise your rights: contact.aelvir@proton.me. You may also lodge a complaint with the CNIL.",
        "The data controller is Aëlvir, a simplified joint-stock company with variable capital, 7 Rue Auguste Rodin, 28630 Le Coudray, France.",
      ],
    },
    submit: "Send my request",
    sending: "Sending…",
    toast: {
      required: "Please fill in all required fields.",
      fieldRequired: "This field is required.",
      success: "Message sent! We'll get back to you shortly.",
      mocked: "Form OK, but email is not configured (SMTP / .env).",
      error: "An error occurred. Please try again.",
      fail: "Sending failed. Check your details and try again.",
    },
  },

  footer: {
    nav: "Navigation",
    product: "The product",
    contact: "Details",
    rights: "All rights reserved.",
  },

  legal: {
    trigger: "Legal notice",
    title: "Legal notice",
    sections: [
      {
        h: "1. Publisher",
        p: [
          "The website https://aelvir.fr is published by Aëlvir, a simplified joint-stock company with variable capital (société par actions simplifiée à capital variable), with a minimum share capital of one hundred (100) euros, whose registered office is at 7 Rue Auguste Rodin, 28630 Le Coudray, France.",
          "Registered with the Chartres Trade and Companies Register (RCS Chartres) under number 939 631 289. SIRET: 939 631 289 00013. Intra-community VAT number: FR62939631289. APE / NAF code: 5821Z, publishing of electronic games.",
          "Contact: contact.aelvir@proton.me. Telephone: 06 60 98 48 16 (president).",
        ],
      },
      {
        h: "2. Publication director",
        p: "Alain Brochen, in his capacity as publication director.",
      },
      {
        h: "3. Hosting",
        p: "The website is hosted by Infomaniak Network SA, whose registered office is at Les Acacias, 1227 Geneva, Switzerland, registered with the Commercial Register of the Canton of Geneva under number CH-660.0.059.996-1. IDE / VAT number: EU442008675. Telephone: +41 22 820 35 44.",
      },
      {
        h: "4. Data-storage subcontractors",
        p: [
          "Storage of the website files (pages, images, scripts) and delivery of e-mails from the contact form are provided by the host Infomaniak Network SA, whose details appear in section 3. No separate CDN or cloud service is used. The website does not use a database.",
          "Messages received at the contact address are stored in a Proton Mail mailbox operated by Proton AG, Route de la Galaise 32, 1228 Plan-les-Ouates, Switzerland. Contact: https://proton.me",
        ],
      },
      {
        h: "5. Intellectual property",
        p: [
          "Unless otherwise stated, all elements accessible on the website, including texts, graphics, logos, icons, images, videos, software, databases, documents, sound elements and their layout, are protected by intellectual property rights and are the exclusive property of Aëlvir or are used under a regular right of use.",
          "Any reproduction, representation, adaptation, translation, extraction, reuse, exploitation or distribution, in whole or in part, of the website or its contents, on any medium whatsoever, without Aëlvir's prior written authorisation, is prohibited, except where mandatory legal provisions provide otherwise.",
        ],
      },
      {
        h: "6. Text and data mining (TDM)",
        p: [
          "Aëlvir objects, under the conditions provided by the French Intellectual Property Code, to any text and data mining of the contents of this website, unless prior written authorisation has been granted.",
          "This objection may be expressed in particular by these legal notices, by the website terms of use, by rights reservations, or by any machine-readable means.",
        ],
      },
      {
        h: "7. Cookies and similar technologies",
        p: "The use of cookies and similar technologies on the website is described in the Privacy policy.",
      },
      {
        h: "8. Personal data",
        p: [
          "Aëlvir processes personal data collected via the website in accordance with applicable data-protection law. Users may exercise their rights at: contact.aelvir@proton.me.",
          "For more information on processing, purposes, legal bases, recipients, retention periods and how to exercise rights, please consult the Privacy policy published on the website.",
        ],
      },
      {
        h: "9. Liability",
        p: [
          "Aëlvir endeavours to ensure the accuracy and updating of the information published on the website. It cannot, however, guarantee completeness, the absence of errors, or the permanent availability of the website and all of its contents.",
          "Users remain solely responsible for their use of the information accessible on the website.",
        ],
      },
      {
        h: "10. Governing law",
        p: "These legal notices are governed by French law.",
      },
      {
        h: "Contact",
        p: "For any question regarding these legal notices: contact.aelvir@proton.me or 06 60 98 48 16.",
      },
    ],
  },

  privacy: {
    trigger: "Privacy policy",
    title: "Privacy policy",
    updated: "Last updated: 4 September 2026",
    sections: [
      {
        h: "1. Data controller",
        p: [
          "The controller of personal data collected on the website aelvir.fr is:",
          "AËLVIR, a simplified joint-stock company (SAS) with variable capital, publisher of the website aelvir.fr.",
          "Registered office: 7 Rue Auguste Rodin, 28630 Le Coudray, France.",
          "RCS Chartres: 939 631 289.",
          "SIRET: 93963128900013.",
          "Intra-community VAT: FR62939631289.",
          "E-mail: contact.aelvir@proton.me.",
          "Telephone: 06 60 98 48 16.",
        ],
      },
      {
        h: "2. Purpose of this policy",
        p: [
          "This privacy policy informs anyone browsing aelvir.fr or using its services how AËLVIR collects, uses, stores and protects their personal data.",
          "It applies in particular to processing carried out in connection with:",
          "browsing the website;",
          "use of the contact form, together with specific information displayed at the time of entry;",
          "exchanges with AËLVIR relating to a request, a project or support.",
        ],
      },
      {
        h: "3. Categories of data that may be collected",
        p: [
          "Depending on the features actually used, AËLVIR may collect the following categories of data:",
          "identification data: first name and last name;",
          "contact details: professional e-mail address;",
          "request-related data: company name, need selected in the form drop-down, content of the message;",
          "technical data: IP address, technical logs, browser type, device used, security information, data needed for the website to function;",
          "any other data voluntarily provided by the user in an exchange with AËLVIR.",
          "AËLVIR takes care to collect only data that is relevant, adequate and limited to what is necessary for the purposes pursued.",
        ],
      },
      {
        h: "4. Purposes of processing",
        p: [
          "Personal data may be processed in order to:",
          "respond to requests sent via the contact form and route them, according to the need selected, to the appropriate contact;",
          "manage pre-contractual exchanges with prospects;",
          "host, operate, maintain and secure the website;",
          "handle requests to exercise rights relating to personal data;",
          "comply with applicable legal, regulatory and evidential obligations;",
          "establish proof of a right and manage, where applicable, pre-litigation and litigation.",
        ],
      },
      {
        h: "5. Legal bases for processing",
        p: [
          "Depending on the case, the processing carried out relies on one or more of the following legal bases:",
          "the data subject's consent, expressed by voluntarily sending the contact form (Article 6(1)(a) GDPR);",
          "AËLVIR's legitimate interest, in particular to secure the website, organise its activity, defend its rights and manage its relationship with prospects.",
        ],
      },
      {
        h: "6. Mandatory or optional nature of the data",
        p: [
          "In the contact form, all fields are mandatory: first name and last name, professional e-mail address, company, choice of need and message.",
          "This data is necessary to handle the request: without it, AËLVIR cannot process the request.",
        ],
      },
      {
        h: "7. Recipients of the data",
        p: [
          "Personal data is accessible, within the limits of their respective duties, to authorised persons within AËLVIR.",
          "It may also be disclosed to providers acting on behalf of AËLVIR, in particular:",
          "Infomaniak, for website hosting and sending messages submitted through the contact form;",
          "Proton, for storing messages received in the contact mailbox;",
          "more generally, any technical provider strictly necessary for the operation of the website, system security or compliance with legal obligations.",
          "The data may also be transmitted to any authority, administration, court, legal auxiliary or adviser where the law so requires or where such disclosure is necessary to defend AËLVIR's rights and interests.",
        ],
      },
      {
        h: "8. Data transfers",
        p: "Where data is processed or made accessible from a country outside the European Union or the European Economic Area, AËLVIR ensures that such processing is framed in accordance with applicable rules, using an appropriate legal mechanism where one is required.",
      },
      {
        h: "9. Retention periods",
        p: [
          "Personal data is kept for no longer than is necessary for the purposes for which it is collected, plus, where applicable, the periods needed to meet legal or evidential obligations or to defend AËLVIR's rights.",
          "In practice:",
          "contact and request data is kept for the time needed to handle the request, then for a maximum of three years from the last exchange, unless earlier deletion is requested;",
          "logs and technical data are kept for the period useful for website security, service continuity and the recording of incidents;",
          "data needed to manage a dispute is kept for the entire duration of the proceedings and until all avenues of appeal are exhausted.",
          "When retention is no longer necessary, the data is deleted, anonymised or securely archived where that is legally permitted or required.",
        ],
      },
      {
        h: "10. Security and confidentiality",
        p: [
          "AËLVIR implements appropriate technical and organisational measures to preserve the security, integrity and confidentiality of personal data and to prevent it from being distorted, damaged, disclosed to unauthorised third parties or accessed without authorisation.",
          "However, as no transmission or storage system offers absolute security, AËLVIR cannot guarantee perfect security in all circumstances.",
        ],
      },
      {
        h: "11. Rights of data subjects",
        p: "In accordance with applicable rules, any data subject has, depending on their situation, the following rights:",
        ul: [
          "right of access;",
          "right to rectification;",
          "right to erasure;",
          "right to restriction of processing;",
          "right to object;",
          "right to data portability where that right applies;",
          "right to withdraw consent at any time where processing is based on consent, without affecting the lawfulness of processing carried out before that withdrawal;",
          "right to lodge a complaint with the CNIL.",
        ],
      },
      {
        h: "12. Exercising these rights",
        p: [
          "Rights may be exercised by sending a request to: contact.aelvir@proton.me",
          "The request must allow AËLVIR to reasonably identify its author before it is processed. In the event of reasonable doubt as to the requester's identity, additional proof may be requested to the extent strictly necessary.",
          "If, after contacting AËLVIR, the data subject considers that their rights are not being respected, they may lodge a complaint with the CNIL.",
        ],
      },
      {
        h: "13. Cookies and similar technologies",
        p: [
          "As of the date of this policy, the website does not use non-essential cookies or similar technologies subject to prior consent.",
          "The website may use cookies or similar technologies that are strictly necessary for its operation, security, user authentication, storing technical choices expressed by the user, or providing a service expressly requested.",
          "The website also uses Matomo, an audience-measurement solution self-hosted by AËLVIR on stats.cywiz.fr, configured so as to benefit from the CNIL exemption from consent for audience-measurement tools. This audience measurement is limited to producing anonymised statistics on visits and use of the website, solely in order to improve its operation and content.",
          "Data collected in this context is not used for advertising purposes, is not combined with other processing, and does not allow the user's browsing to be tracked on other websites.",
          "In accordance with the requirements applicable to this setup, you may object at any time to Matomo tracking your browsing via the opt-out module available at: https://stats.cywiz.fr/index.php?module=CoreAdminHome&action=optOut&language=en",
          "If AËLVIR later introduces cookies or similar technologies subject to prior consent, specific information will be displayed and the user's consent will be collected where required.",
        ],
      },
      {
        h: "14. Updates to this policy",
        p: [
          "AËLVIR may amend this privacy policy at any time, in particular to take account of a change to the website, its features, its providers or applicable rules.",
          "The version in force is the one published on the website on the date it is consulted.",
        ],
      },
    ],
  },

  cgu: {
    trigger: "Terms",
    title: "Terms of use",
    sections: [
      {
        h: "What this site is for",
        p: [
          "aelvir.fr presents the Aëlvir studio and lets you write to us. That is all: no shop, no customer area, no online checkout.",
          "You will find what the studio builds (interactive experiences, serious games, simulation, virtual reality) and Cywiz, the cybersecurity awareness application it publishes. The substance of a project is discussed afterwards, off the site, and if needed in a separate contract.",
          "Pages can change (copy, visuals, how the offer is worded) without these terms being rewritten each time, as long as the site remains a showcase and a way to get in touch.",
          "Reading the site or sending the form means you accept these terms as shown that day. Who publishes the site, who hosts it, and the other mandatory particulars live in the Legal notice, in the footer, not here.",
        ],
      },
      {
        h: "What you may do, and what you may not",
        p: [
          "You may read the pages and write to us through the form (a project, a question, a quote request). Access is free; your connection and equipment are yours to pay for.",
          "There is no account to create, so no login, no profile to delete, no comments and no forum. Nobody publishes on this site except Aëlvir.",
          "You may not use the site to deceive, harass, scrape content at scale (including with a bot or text-and-data mining), inject hostile code, or fill the form with false information. If you do, you answer for it. Aëlvir may ignore the request and, if needed, go to court.",
          "An outage, maintenance window or technical incident can take the site down without notice. Aëlvir does not guarantee that will never happen.",
        ],
      },
      {
        h: "Your data",
        p: "The only place the site collects personal data is the form: name, organisation, e-mail, request type, message. To access, correct, erase or object: contact.aelvir@proton.me. The rest (purposes, retention, recipients, cookies and audience measurement) is in the Privacy policy.",
      },
      {
        h: "What belongs to Aëlvir",
        p: "Texts, images, logo, graphics, code: all of it is protected. Aëlvir owns it, or uses it under a regular right. Reading it here is allowed. Copying it, reusing it, or turning it to commercial or advertising use, without written consent, is not.",
      },
      {
        h: "Where our liability stops",
        p: [
          "We take care over what we publish. That does not rule out a mistake, a gap, or something that has gone out of date. Nothing on the site is a contract. How you read it is on you.",
          "Aëlvir is not liable for an event outside the website (network failure, force majeure, act of a third party). Protecting your own equipment remains your responsibility.",
          "Some links may lead to websites or services published by third parties. In that case, Aëlvir does not control their content, updates or practices. If an unknown website links to aelvir.fr, Aëlvir does not answer for that either.",
          "We protect form data as far as is reasonably possible. Nobody can promise absolute security.",
        ],
      },
      {
        h: "If we disagree",
        p: "These terms are governed by French law. Before any court, we look for an agreement first. If that fails, the competent French courts decide. The terms apply for as long as you use the site.",
      },
    ],
  },
};
